import { useMemo, useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import {
    Baseline,
    Copy as CopyIcon,
    Eraser,
    ShieldCheck,
    Sparkles,
    Timer as TimerIcon,
    Type as TypeIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const FAQS = [
    { q: 'How does it count words?', a: 'We split the text on whitespace after trimming, and count runs of non-whitespace characters. This matches the way Word and Google Docs count words for most practical purposes.' },
    { q: 'How is reading time estimated?', a: 'We use 225 words per minute for general reading. That’s an industry-standard average — comfortable for adult English readers. Speaking time uses 145 words per minute.' },
    { q: 'Does anything I type leave my browser?', a: 'No. This tool is 100% client-side. Your text is never sent to our servers and nothing is logged. Reload the page and it’s gone.' },
    { q: 'Does it count characters with or without spaces?', a: 'Both. We show characters including spaces (the natural “how long is this?” number) and characters without spaces (used by some length-limited fields like meta descriptions).' },
    { q: 'Can I paste from Word or Google Docs?', a: 'Yes. Formatting is stripped automatically — only the text counts.' },
];

const HOW = [
    'Type or paste any block of text into the editor. As you type, the counter updates instantly: words, characters (with and without spaces), sentences, paragraphs, and estimated reading and speaking time. There’s no button to press, no upload, no signup — just a live count.',
    'The tool runs entirely inside your browser. Nothing you type is ever sent to a server, and nothing is stored anywhere. That means you can safely paste in a confidential draft, a client email, or an entire manuscript, and none of it leaves your device.',
    'Word Counter uses the same tokenization approach that Microsoft Word and Google Docs use for their built-in counters: whitespace-separated tokens after trimming. That keeps the numbers consistent with what you’d see in your day-to-day writing tools, so estimates for essays, articles, and social posts stay predictable.',
    'Reading time is estimated using 225 words per minute — the widely-cited average for adult English readers on the web. Speaking time uses 145 words per minute, which is a comfortable pace for spoken presentations. Both are approximations, so use them as a guide rather than a stopwatch.',
];
const BENEFITS = [
    'For writers, editors, and students, exact counts matter. Essays have word ceilings, LinkedIn posts have hard character limits, meta descriptions have a soft cap around 160 characters, and tweets are pinned to 280. A word counter that runs beside your draft prevents nasty surprises just before you hit publish.',
    'For content marketers, the reading-time estimate is especially useful. Modern readers scan; you have about 15 seconds to convince them a piece is worth their time. Seeing “4 min read” on a draft is a nudge to consider whether it should really be 8 minutes long, or whether it could be split into two focused pieces.',
    'For speech writers and podcasters, an accurate speaking-time estimate is priceless. 800 words comes out to about 5 minutes and 30 seconds at a natural spoken pace. Use it to plan pauses, sections, and total runtime before you ever hit record.',
    'And because UtilityTool’s Word Counter is fully client-side, you can use it in situations where privacy actually matters: a resume you’re about to publish, a company memo, or a story you’re still shopping around. Nothing you paste in ever leaves your device.',
];
const TIPS = [
    'Use “Characters (no spaces)” for meta descriptions and social bios where each character costs you.',
    'Aim for a reading time your audience can actually invest — 4–6 minutes is a sweet spot for most articles.',
    'Copy the stats out with the Copy button if you need to paste them into a brief.',
];

function computeStats(text) {
    const trimmed = text.trim();
    const words = trimmed.length === 0 ? 0 : trimmed.split(/\s+/).filter(Boolean).length;
    const chars = text.length;
    const charsNoSpaces = text.replace(/\s/g, '').length;
    const sentences =
        trimmed.length === 0
            ? 0
            : (trimmed.match(/[^.!?…]+[.!?…]+(?:\s|$)|[^.!?…]+$/g) || []).length;
    const paragraphs = trimmed.length === 0 ? 0 : text.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).length;
    const readingSeconds = Math.round((words / 225) * 60);
    const speakingSeconds = Math.round((words / 145) * 60);
    return { words, chars, charsNoSpaces, sentences, paragraphs, readingSeconds, speakingSeconds };
}

function fmtDuration(seconds) {
    if (!seconds || seconds < 1) return '0 sec';
    if (seconds < 60) return `${seconds} sec`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m < 60) return s === 0 ? `${m} min` : `${m} min ${s} sec`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h} hr ${mm} min`;
}

export default function WordCounterPage() {
    const [text, setText] = useState('');
    const stats = useMemo(() => computeStats(text), [text]);

    const clear = () => setText('');
    const copyStats = async () => {
        const summary = [
            `Words: ${stats.words}`,
            `Characters: ${stats.chars} (no spaces: ${stats.charsNoSpaces})`,
            `Sentences: ${stats.sentences}`,
            `Paragraphs: ${stats.paragraphs}`,
            `Reading time: ${fmtDuration(stats.readingSeconds)}`,
            `Speaking time: ${fmtDuration(stats.speakingSeconds)}`,
        ].join('\n');
        try {
            await navigator.clipboard.writeText(summary);
            toast.success('Stats copied to clipboard.');
        } catch {
            toast.error('Could not access clipboard. Copy manually.');
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Word Counter',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="Word Counter"
                description="Count words, characters, sentences, paragraphs, and reading time in real time. 100% client-side — your text never leaves your browser."
                canonical="/tools/word-counter"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Word Counter' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <TypeIcon className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                            Word Counter
                        </h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Live word, character, sentence, and reading-time counts. Runs
                            entirely in your browser.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">In-browser</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">No upload</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Live</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.35fr_0.65fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80">
                        <label htmlFor="wc-input" className="text-sm font-medium text-slate-800">
                            Your text
                        </label>
                        <Textarea
                            id="wc-input"
                            data-testid="tool-word-counter-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={14}
                            placeholder="Paste or type your text here. The counters update as you type."
                            className="mt-2 resize-y min-h-[280px] font-normal leading-relaxed"
                        />
                        <div className="mt-4 flex flex-wrap gap-2">
                            <Button
                                onClick={copyStats}
                                variant="secondary"
                                data-testid="tool-word-counter-copy"
                                disabled={stats.words === 0}
                            >
                                <CopyIcon className="w-4 h-4 mr-2" /> Copy stats
                            </Button>
                            <Button
                                onClick={clear}
                                variant="ghost"
                                data-testid="tool-word-counter-clear"
                                disabled={text.length === 0}
                                className="text-slate-600"
                            >
                                <Eraser className="w-4 h-4 mr-2" /> Clear
                            </Button>
                        </div>
                    </Card>

                    <aside className="space-y-4">
                        <Card className="p-5 border-border/80" data-testid="tool-word-counter-stats">
                            <p className="text-sm font-semibold text-slate-900">Live stats</p>
                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <StatCell testid="wc-stat-words" label="Words" value={stats.words} icon={Sparkles} />
                                <StatCell testid="wc-stat-chars" label="Characters" value={stats.chars} icon={Baseline} />
                                <StatCell testid="wc-stat-chars-nospace" label="Chars (no spaces)" value={stats.charsNoSpaces} icon={Baseline} />
                                <StatCell testid="wc-stat-sentences" label="Sentences" value={stats.sentences} icon={TypeIcon} />
                                <StatCell testid="wc-stat-paragraphs" label="Paragraphs" value={stats.paragraphs} icon={TypeIcon} />
                                <StatCell testid="wc-stat-reading" label="Read time" value={fmtDuration(stats.readingSeconds)} icon={TimerIcon} muted />
                                <StatCell testid="wc-stat-speaking" label="Speak time" value={fmtDuration(stats.speakingSeconds)} icon={TimerIcon} muted />
                            </div>
                        </Card>
                        <Card className="p-5 border-border/80 bg-[hsl(var(--surface-2))]">
                            <div className="flex items-center gap-2 text-slate-800">
                                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Nothing leaves your browser</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                This tool runs entirely on your device. Your text is never
                                sent to us and nothing is stored anywhere.
                            </p>
                        </Card>
                        <Card className="p-5 border-border/80">
                            <p className="text-sm font-semibold text-slate-900">Related</p>
                            <ul className="mt-2 space-y-1.5 text-sm">
                                <li><a href="/tools/case-converter" className="text-slate-600 hover:text-slate-900">Case Converter (soon)</a></li>
                                <li><a href="/tools/lorem-ipsum" className="text-slate-600 hover:text-slate-900">Lorem Ipsum (soon)</a></li>
                                <li><a href="/blog/a-quiet-guide-to-writing-great-alt-text" className="text-[hsl(var(--primary))] hover:underline">Guide: writing great alt text</a></li>
                            </ul>
                        </Card>
                    </aside>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}

function StatCell({ testid, label, value, icon: Icon, muted }) {
    return (
        <div
            data-testid={testid}
            className="rounded-lg border border-border/70 bg-white p-3"
        >
            <div className="flex items-center gap-1.5 text-slate-500">
                <Icon className="w-3.5 h-3.5" />
                <span className="text-xs">{label}</span>
            </div>
            <p
                className={`mt-1 text-lg font-semibold tabular-nums ${
                    muted ? 'text-slate-700' : 'text-slate-900'
                }`}
            >
                {value}
            </p>
        </div>
    );
}
